// Services
import { getUserId } from "app/services/LoginService";

const opsExceptionFormConfig = ({ mode } = {}) => {
	if (!mode || mode === "EDIT") {
		return {
			items: [
				{
					type: "section",
					label: "Exception Details",
					section: { classes: { label: " -mt-1 " } },
				},
				{
					type: "group",
					content: {
						items: [
							{
								key: "title",
								type: "text",
								label: "Title",
								required: true,
								placeholder: "Enter exception title",
							},
							{
								key: "category",
								type: "select",
								label: "Category",
								required: true,
								options: [
									{ value: "Execution", label: "Execution" },
									{ value: "Booking", label: "Booking" },
									{ value: "Assignment", label: "Assignment" },
								],
								defaultValue: "Execution",
							},
						],
					},
				},
				{
					type: "group",
					content: {
						items: [
							{
								key: "priority",
								type: "select",
								label: "Priority",
								required: true,
								options: [
									{ value: "Low", label: "Low" },
									{ value: "Medium", label: "Medium" },
									{ value: "High", label: "High" },
									{ value: "Critical", label: "Critical" },
								],
								defaultValue: "Medium",
							},
							{
								key: "status",
								type: "select",
								label: "Status",
								required: true,
								options: [
									{ value: "Open", label: "Open" },
									{ value: "In Progress", label: "In Progress" },
									{ value: "Resolved", label: "Resolved" },
									{ value: "Closed", label: "Closed" },
								],
								defaultValue: "Open",
							},
						],
					},
				},
				{
					type: "group",
					content: {
						items: [
							{
								key: "description",
								type: "text",
								label: "Description",
								placeholder: "Enter exception description",
								multiline: { rows: 3 },
							},
						],
					},
				},
				{
					type: "section",
					label: "References",
				},
				{
					type: "group",
					content: {
						items: [
							{
								key: "trip__view",
								type: "autocomplete",
								label: "Trip (Optional)",
								placeholder: "Search for trip",
								autocomplete: {
									provider: "smarthop",
									url: "api/profile/carriers/:carrierId/trips/search",
									searchKey: "search",
									resultKey: "items",
									minChars: 2,
								},
								description: "Link this exception to a specific trip",
							},
						],
					},
				},
				{
					type: "group",
					content: {
						items: [
							{
								key: "truck__view",
								type: "autocomplete",
								label: "Truck (Optional)",
								placeholder: "Search for truck",
								autocomplete: {
									provider: "smarthop",
									url: "api/profile/carriers/:carrierId/trucks/search",
									searchKey: "search",
									resultKey: "items",
									minChars: 2,
								},
								description: "Link this exception to a specific truck",
							},
						],
					},
				},
				{
					type: "section",
					label: "Additional Information",
				},
				{
					type: "group",
					content: {
						items: [
							{
								key: "comments",
								type: "text",
								label: "Comments",
								placeholder: "Enter any additional comments",
								multiline: { rows: 4 },
							},
						],
					},
				},
				{
					type: "group",
					content: {
						items: [
							{
								key: "resolution_notes",
								type: "text",
								label: "Resolution Notes",
								placeholder: "Enter resolution notes (if resolved)",
								multiline: { rows: 3 },
								isVisible: (item) => item?.status === "Resolved" || item?.status === "Closed",
							},
						],
					},
				},
				{
					type: "group",
					content: {
						items: [
							{
								key: "assigned_to__view",
								type: "autocomplete",
								label: "Assigned To",
								placeholder: "Search for user",
								autocomplete: {
									provider: "smarthop",
									url: "api/profile/carriers/:carrierId/users/search",
									searchKey: "search",
									resultKey: "items",
									minChars: 2,
								},
								description: "Assign this exception to a user",
							},
						],
					},
				},
				{ type: "action", action: "SUBMIT" },
			],
		};
	} else if (mode === "VIEW") {
		return {
			info: {
				items: [
					{
						key: "title",
						type: "text",
						label: "Title",
					},
					{
						key: "category",
						type: "text",
						label: "Category",
					},
					{
						key: "priority",
						type: "text",
						label: "Priority",
					},
					{
						key: "status",
						type: "text",
						label: "Status",
					},
					{
						key: "description",
						type: "textarea",
						label: "Description",
					},
					{
						key: "comments",
						type: "textarea",
						label: "Comments",
					},
					{
						key: "resolution_notes",
						type: "textarea",
						label: "Resolution Notes",
					},
					{
						key: "resolution_date",
						type: "date",
						label: "Resolution Date",
					},
					{
						key: "createdAt",
						type: "date",
						label: "Created At",
					},
					{
						key: "updatedAt",
						type: "date",
						label: "Updated At",
					},
				],
			},
		};
	}
};

const opsExceptionForm = ({ user } = {}) => {
	return {
		urlPOST: "api/profile/carriers/:carrierId/ops-exceptions",
		urlGET: "api/profile/carriers/:carrierId/ops-exceptions/:id",
		urlPUT: "api/profile/carriers/:carrierId/ops-exceptions/:id",
		urlDELETE: "api/profile/carriers/:carrierId/ops-exceptions/:id",
		idKey: "_id",
		triggerEvent: ["opsExceptionRevision", "profileRevision", "dataRevision"],
		listenEvent: "opsExceptionRevision",
		header: {
			create: { label: "Add New Ops Exception" },
			edit: { label: "Edit Ops Exception" },
			delete: { label: "Delete Ops Exception" },
			view: { label: "View Ops Exception" },
		},
		messages: {
			edit: { success: "Successfully Saved Exception" },
			create: { sameAs: "edit" },
		},
		content: {
			delete: {
				items: [
					{
						type: "message",
						builder: (item) => `Would you like to permanently delete exception '${item.title}'?`,
					},
					{
						type: "group",
						content: {
							items: [
								{ type: "action", action: "CANCEL" },
								{ type: "action", action: "DELETE" },
							],
						},
					},
				],
			},
			view: {
				sameAs: "edit",
			},
			create: {
				...opsExceptionFormConfig({ mode: "EDIT" }),
			},
			edit: opsExceptionFormConfig({ mode: "EDIT" }),
		},
	};
};

export { opsExceptionForm, opsExceptionFormConfig };
